import React from 'react';
import {
  Box,
  Button,
  Checkbox,
  Divider,
  FormControlLabel,
  IconButton,
  Paper,
  Snackbar,
  Stack,
  TextField,
  Typography,
  styled,
  useMediaQuery,
  useTheme,
} from '@mui/material';
import CheckIcon from '@mui/icons-material/Check';
import CloseIcon from '@mui/icons-material/Close';
import { useTranslation } from 'react-i18next';

import { submitNpsQuestionnaire } from './submitNpsQuestionnaire';

const AUTO_CLOSE_AFTER_SUBMIT_MS = 3000;
const NPS_PRIMARY = '#0B416E';
const NPS_PRIMARY_DARK = '#083558';
const NPS_MUTED_TEXT = '#757575';
const NPS_SCORE_BORDER_RADIUS = '6px';
const NPS_SUBMIT_BORDER_RADIUS = '4px';
const SCORE_COUNT = 11;

const ScoreButton = styled('button', {
  shouldForwardProp: (prop) => prop !== 'selected' && prop !== 'scoreFontSize',
})<{ selected: boolean; scoreFontSize: string }>(({ selected, scoreFontSize }) => ({
  flex: '1 1 0',
  minWidth: 0,
  aspectRatio: '1 / 1',
  margin: 0,
  padding: 0,
  boxSizing: 'border-box',
  border: '1px solid',
  borderColor: selected ? NPS_PRIMARY : '#e0e0e0',
  borderRadius: NPS_SCORE_BORDER_RADIUS,
  backgroundColor: selected ? NPS_PRIMARY : '#fff',
  color: selected ? '#fff' : '#212121',
  fontWeight: 700,
  fontSize: scoreFontSize,
  lineHeight: 1,
  fontFamily: 'inherit',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  cursor: 'pointer',
  appearance: 'none',
  WebkitAppearance: 'none',
  transition: 'background-color 0.15s, border-color 0.15s, color 0.15s',
  '&:hover:not(:disabled)': {
    borderColor: NPS_PRIMARY,
  },
  '&:disabled': {
    opacity: 0.6,
    cursor: 'default',
  },
}));

function useScoreScaleMetrics() {
  const theme = useTheme();
  const veryCompact = useMediaQuery('(max-width:360px)');
  const compact = useMediaQuery(theme.breakpoints.down('sm'));
  const gap = veryCompact ? 4 : compact ? 6 : 8;
  const fontSize = veryCompact ? '0.85rem' : compact ? '0.95rem' : '1rem';

  return { gap, fontSize };
}

const CONTENT_WIDTH = 600;

type Props = {
  open: boolean;
  appCode: string;
  appName: string;
  apiBaseUrl: string;
  questionnaireId: string;
  onClose: () => void;
};

/**
 * Questionario NPS non bloccante (basso a destra): scala 0–10, commento opzionale,
 * consenso contatto, POST submission. Si chiude con voto inviato o con la X.
 */
const NpsQuestionnaireModal = ({
  open,
  appCode,
  appName,
  apiBaseUrl,
  questionnaireId,
  onClose,
}: Props) => {
  const { t } = useTranslation();
  const { gap, fontSize } = useScoreScaleMetrics();
  const [score, setScore] = React.useState<number | null>(null);
  const [comment, setComment] = React.useState('');
  const [contactConsent, setContactConsent] = React.useState(true);
  const [submitting, setSubmitting] = React.useState(false);
  const [submitted, setSubmitted] = React.useState(false);
  const [submitError, setSubmitError] = React.useState(false);

  React.useEffect(() => {
    if (!open) {
      setScore(null);
      setComment('');
      setContactConsent(true);
      setSubmitting(false);
      setSubmitted(false);
      setSubmitError(false);
    }
  }, [open]);

  const onCloseRef = React.useRef(onClose);
  onCloseRef.current = onClose;

  React.useEffect(() => {
    if (!submitted) return;
    const timerId = window.setTimeout(
      () => onCloseRef.current(),
      AUTO_CLOSE_AFTER_SUBMIT_MS,
    );
    return () => window.clearTimeout(timerId);
  }, [submitted]);

  const handleSubmit = async () => {
    if (score === null || submitting) return;
    setSubmitting(true);
    setSubmitError(false);

    const result = await submitNpsQuestionnaire({
      apiBaseUrl,
      appCode,
      questionnaireId,
      score,
      comment,
      contactConsent,
    });

    setSubmitting(false);
    if (result.ok) {
      setSubmitted(true);
    } else {
      setSubmitError(true);
    }
  };

  const formDisabled = submitting || submitted;

  return (
    <Snackbar
      open={open}
      anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
      ClickAwayListenerProps={{ mouseEvent: false, touchEvent: false }}
      disableWindowBlurListener
      sx={{
        pointerEvents: 'none',
        zIndex: (theme) => theme.zIndex.modal - 1,
        maxWidth: { xs: 'calc(100% - 16px)', sm: CONTENT_WIDTH },
      }}
    >
      <Paper
        elevation={8}
        role="dialog"
        aria-modal="false"
        aria-labelledby="nps-survey-title"
        sx={{
          pointerEvents: 'auto',
          position: 'relative',
          width: { xs: '100%', sm: CONTENT_WIDTH },
          maxWidth: '100%',
          maxHeight: 'calc(100vh - 48px)',
          overflowX: 'hidden',
          overflowY: 'auto',
          boxSizing: 'border-box',
          borderRadius: 2,
          px: { xs: 2, sm: 3 },
          pt: 4,
          pb: 3,
        }}
      >
        <IconButton
          onClick={onClose}
          aria-label={t('npsSurvey.close')}
          sx={{
            position: 'absolute',
            top: 8,
            right: 8,
            color: 'grey.500',
          }}
        >
          <CloseIcon fontSize="small" />
        </IconButton>

        {submitted ? (
          <Box sx={{ textAlign: 'center', py: 3, px: 1 }}>
            <CheckIcon
              sx={{
                fontSize: 48,
                color: 'text.primary',
                mb: 2,
              }}
            />
            <Typography
              id="nps-survey-title"
              variant="h6"
              sx={{
                fontWeight: 700,
                mb: 1.5,
                color: 'text.primary',
              }}
            >
              {t('npsSurvey.thanksTitle')}
            </Typography>
            <Typography variant="body2" color="text.secondary">
              {t('npsSurvey.thanksMessage', { appName })}
            </Typography>
          </Box>
        ) : (
          <Stack spacing={2.5} sx={{ pt: 1, width: '100%' }}>
            <Stack spacing={1.5}>
            <Typography
              id="nps-survey-title"
              variant="h6"
              sx={{
                fontWeight: 700,
                textAlign: 'center',
                color: 'grey.900',
                width: '100%',
              }}
            >
              {t('npsSurvey.title')}
            </Typography>
            <Typography
              variant="body2"
              sx={{
                textAlign: 'center',
                px: 1,
                width: '100%',
                color: NPS_MUTED_TEXT,
              }}
            >
              {t('npsSurvey.question', { appName })}
            </Typography>
          </Stack>

          <Stack spacing={0.75} sx={{ width: '100%' }}>
            <Stack
              direction="row"
              spacing={`${gap}px`}
              sx={{ width: '100%' }}
            >
              {Array.from({ length: SCORE_COUNT }, (_, i) => {
                const selected = score === i;
                return (
                  <ScoreButton
                    type="button"
                    key={i}
                    selected={selected}
                    scoreFontSize={fontSize}
                    onClick={() => setScore(i)}
                    disabled={formDisabled}
                    aria-pressed={selected}
                    aria-label={String(i)}
                  >
                    {i}
                  </ScoreButton>
                );
              })}
            </Stack>

            <Stack direction="row" justifyContent="space-between">
              <Typography variant="caption" sx={{ color: NPS_MUTED_TEXT }}>
                {t('npsSurvey.low')}
              </Typography>
              <Typography variant="caption" sx={{ color: NPS_MUTED_TEXT }}>
                {t('npsSurvey.high')}
              </Typography>
            </Stack>
          </Stack>

          <Divider />

          {score !== null ? (
            <TextField
              label={t('npsSurvey.comment')}
              value={comment}
              onChange={(e) => setComment(e.target.value)}
              fullWidth
              multiline
              minRows={2}
              disabled={formDisabled}
              sx={{ mb: 1.5 }}
            />
          ) : null}

          <FormControlLabel
            sx={{ mb: 0, mx: 0, alignItems: 'center' }}
            control={
              <Checkbox
                checked={contactConsent}
                onChange={(e) => setContactConsent(e.target.checked)}
                disabled={formDisabled}
                sx={{
                  color: 'grey.400',
                  '&.Mui-checked': { color: NPS_PRIMARY },
                }}
              />
            }
            label={
              <Typography variant="body2" color="text.primary">
                {t('npsSurvey.contactConsent')}
              </Typography>
            }
          />
          {submitError ? (
            <Typography sx={{ mt: 1 }} variant="body2" color="error.main">
              {t('npsSurvey.submitError')}
            </Typography>
          ) : null}
          <Button
            fullWidth
            variant="contained"
            onClick={handleSubmit}
            disabled={score === null || submitting}
            sx={{
              mt: 2,
              py: 1.25,
              borderRadius: NPS_SUBMIT_BORDER_RADIUS,
              bgcolor: NPS_PRIMARY,
              textTransform: 'none',
              fontWeight: 700,
              fontSize: '1rem',
              boxShadow: 'none',
              '&:hover': {
                bgcolor: NPS_PRIMARY_DARK,
                boxShadow: 'none',
              },
              '&.Mui-disabled': {
                bgcolor: 'grey.300',
                color: 'common.white',
              },
            }}
          >
            {t('npsSurvey.submit')}
          </Button>
          </Stack>
        )}
      </Paper>
    </Snackbar>
  );
};

export default NpsQuestionnaireModal;
